import React, { useCallback, useEffect, useMemo } from 'react';
import { translate } from '@shakkerai/util-i18n';
import { useLoadings, getLoadings } from '@shakkerai/pro-store';
import { setProjectState } from '@starflow/app/store';
import { SegmentEditor, useSegmentEditorState } from '@starflow/app/components/common/SegmentEditor';
import { viewportActions } from '@starflow/app/store';
import {
  inferenceAPIBucket,
  cropCanvas,
  getMaskRange,
  scheduleLoadImage,
  uploadCanvas,
  saveImageToBestPosition,
  extractMaskContent,
  canvasToImage,
  // downloadCanvas,
  cropTransparentEdge,
} from '@shakkerai/pro-utils';
import { notifyError, notifySuccess } from '@shakkerai/pro-components';
import { requestCutoutImage } from '@shakkerai/services';
import { getCDNImage } from '@shakkerai/util-image';

const SelectAreaEditor = React.memo((props) => {
  const { element } = props;
  const extracting = useLoadings(element.id + '-extracting');

  const [editorState, setEditorState, getEditorState] = useSegmentEditorState();
  const { selections } = editorState.value;
  const allowExtracting = selections?.length > 0;
  const handleExtracting = useCallback(async () => {
    /*
      提取
      1.把选区找到,先生成蒙版，根据蒙版的颜色找出范围(上下左右)
      2.把选区截出来
      3.对数据去背景
      4.插入到画布
    */
    const { startLoading, stopLoading } = getLoadings();
    startLoading(element.id + '-extracting');
    try {
      const { selections } = getEditorState();
      const sourceImage = await scheduleLoadImage(element.url);
      const { minX, minY, maxX, maxY, maskCanvas } = getMaskRange({
        crop: { x: 0, y: 0, width: element.width, height: element.height },
        selections,
      });
      const maskImage = await canvasToImage(maskCanvas);
      const cropedMaskCanvas = cropCanvas(maskImage, { minX, minY, maxX, maxY });
      const cropedSourceCanvas = cropCanvas(sourceImage, { minX, minY, maxX, maxY });
      const uploadRes = await uploadCanvas(cropedSourceCanvas);
      const { images } = await requestCutoutImage({
        images: [uploadRes.file],
        mode: 1,
        ...inferenceAPIBucket,
      });
      // 去mask之外的
      const removedBackgroundImage = await scheduleLoadImage(getCDNImage(images[0]));
      const extractedCanvas = await extractMaskContent(cropedMaskCanvas, removedBackgroundImage);
      const extractedImage = await canvasToImage(extractedCanvas);
      const noTransparentImageCanvas = cropTransparentEdge(extractedImage);
      const noTransparentUploadResult = await uploadCanvas(noTransparentImageCanvas);
      saveImageToBestPosition({
        url: noTransparentUploadResult.url,
        width: noTransparentImageCanvas.width,
        height: noTransparentImageCanvas.height,
      });
      notifySuccess(translate('image_extract_success'));
      setProjectState({
        currentElementTool: null,
        editingElementId: null,
        selection: [],
      });
    } catch (e) {
      console.error('xxx', e);
      notifyError(translate('image_extract_fail'));
    } finally {
      stopLoading(element.id + '-extracting');
    }

    // console.log('xxx maskRange', maskRange)
  }, [element]);

  const config = useMemo(() => {
    return {
      noSegment: true,
      showToolbar: true,
      noFeathering: true,
      noInvert: true,
      toolbarRightButton: (
        <button
          disabled={!allowExtracting || extracting}
          onClick={handleExtracting}
          className="h-8 ml-2 px-3 disabled:bg-[#999] disabled:cursor-not-allowed bg-primary-dark pointer-events-auto text-white text-xs whitespace-nowrap rounded-md"
        >
          {extracting ? translate('extract_button_loading') : translate('extract_button')}
        </button>
      ),
    };
  }, [extracting, element, allowExtracting]);

  useEffect(() => {
    viewportActions.locateElement(element.id, { animate: true, fitView: true });
  }, [element.id]);

  return (
    <SegmentEditor
      config={config}
      element={element}
      editorState={editorState}
      setEditorState={setEditorState}
      getEditorState={getEditorState}
      className="outline outline-2 outline-primary"
    />
  );
});

export const wrapImageOverlayer = (Overlayer) =>
  React.memo((props) => {
    const { currentElementTool, editingElementId, element } = props;
    const isActive = currentElementTool === 'selectAreaTool' && editingElementId === element.id;
    if (isActive) {
      return <SelectAreaEditor element={element} />;
    }

    return <Overlayer {...props} />;
  });
